// Plunk SSO client — talks to the auth broker at auth.plunk.io (see
// github.com/Plunk/AuthBroker). The broker holds the Cognito refresh token
// server-side behind an httpOnly .plunk.io cookie; apps fetch short-lived
// tokens from it. One sign-in covers studio.plunk.io + app.plunk.io.
//
// Only effective when the app itself runs on *.plunk.io (the cookie is
// same-site scoped) — localhost dev keeps the direct Cognito flow.

const AUTH_BASE = 'https://auth.plunk.io';
const SSO_MODE_KEY = 'plunkSsoMode';
const SSO_TENANT_KEY = 'plunkSsoTenant';
const SSO_TENANT_CONFIG_KEY = 'plunkTenantConfig';
export function ssoEligible() {
  try {
    return window.location.hostname.endsWith('.plunk.io');
  } catch (ex) {
    return false;
  }
}

// SSO-mode flag: set when the current tokens came from the broker, so the
// refresh handler knows to go back to the broker (there is no client-side
// refresh token in SSO mode).
export function isSsoMode() {
  try {
    return localStorage.getItem(SSO_MODE_KEY) === '1';
  } catch (ex) {
    return false;
  }
}
export function setSsoMode(on) {
  try {
    if (on) localStorage.setItem(SSO_MODE_KEY, '1');else localStorage.removeItem(SSO_MODE_KEY);
  } catch (ex) {/* ignore */}
}

// Acting tenant from the broker session. Tenancy is a broker concern:
// privileged users pick it on the auth.plunk.io page (stored server-side in
// the SSO session); non-privileged users get their custom:account. The app
// mirrors it here so account.get() can prefer it over the raw claim.
export function getSsoTenant() {
  try {
    return localStorage.getItem(SSO_TENANT_KEY) || null;
  } catch (ex) {
    return null;
  }
}
export function setSsoTenant(tenant) {
  try {
    if (tenant) localStorage.setItem(SSO_TENANT_KEY, tenant);else localStorage.removeItem(SSO_TENANT_KEY);
  } catch (ex) {/* ignore */}
}

// Per-tenant app config from the broker (label, domain, feed url, …). Apps
// read these facts here instead of hardcoding them per tenant.
export function getSsoTenantConfig() {
  try {
    return JSON.parse(localStorage.getItem(SSO_TENANT_CONFIG_KEY) || 'null');
  } catch (e) {
    return null;
  }
}
export function setSsoTenantConfig(cfg) {
  try {
    if (cfg) localStorage.setItem(SSO_TENANT_CONFIG_KEY, JSON.stringify(cfg));else localStorage.removeItem(SSO_TENANT_CONFIG_KEY);
  } catch (e) {/* ignore */}
}

// Redirect to the central login (auth.plunk.io), returning to `returnHref`
// (default: the current page) after sign-in. Used for both sign-out and any
// unauthenticated access on *.plunk.io — the app's own /Auth/SignIn form is
// localhost-dev only.
export function redirectToCentralLogin(returnHref) {
  const back = returnHref || window.location.origin + window.location.pathname + window.location.search;
  window.location.href = `${AUTH_BASE}/?redirect=${encodeURIComponent(back)}`;
}

// Silently fetch the SSO session. Resolves to
// { idToken, accessToken, expiresAt(ms), claims:{sub,email,account,groups} }
// or null when not signed in / broker unreachable.
export async function fetchSsoSession() {
  try {
    const r = await fetch(`${AUTH_BASE}/api/session`, {
      credentials: 'include'
    });
    if (!r.ok) return null;
    return await r.json();
  } catch (ex) {
    return null;
  }
}

// Sign in THROUGH the broker — establishes the .plunk.io SSO cookie so the
// other Plunk apps are signed in too. Throws {code,message} on failure
// (shape-compatible with the Cognito SDK errors the sign-in view maps).
export async function brokerLogin(email, password) {
  let r;
  try {
    r = await fetch(`${AUTH_BASE}/api/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password
      })
    });
  } catch (ex) {
    const err = new Error('Network error');
    err.code = 'NetworkError';
    throw err;
  }
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(d.error || 'Sign-in failed');
    err.code = r.status === 409 ? 'PasswordResetRequiredException' : 'NotAuthorizedException';
    throw err;
  }
  return d; // { ok, claims }
}

// Kill the SSO session everywhere (revokes the refresh token server-side).
export async function brokerLogout() {
  try {
    await fetch(`${AUTH_BASE}/api/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (ex) {/* best effort */}
}